import { defaultsDeep } from "lodash";
import { thor, Quota } from "../../typings/thor";
import { LokiOrganizationDialogBaseController, Status, STATUS_DEFAULT } from "./organization-dialog-base.controller";
import { isAdvertiser, isQuotaConsumed } from "../../utils/organization";
import { MonextContract } from "../../typings/payment";

export class LokiUpdateOrganizationDialogController extends LokiOrganizationDialogBaseController {
    public static $inject: string[] = [
        "$log",
        "$mdDialog",
        "$q",
        "$scope",
        "$translate",
        "organization",
        "organizationService",
        "quotasService",
        "monextContracts",
        "monextContractService",
        "toaster"
    ];

    public orgaAtlasAccessMaxTokens: number;
    public orgaAtlasMaxTokens: number;
    public monextJudicialContract: MonextContract;
    public monextVoluntaryContract: MonextContract;
    public disableSubmitVoluntary: boolean = false;
    public disableSubmitJudicial: boolean = false;
    public initialMonextVoluntaryContract: MonextContract;
    public initialMonextJudicialContract: MonextContract;
    public organizationForm;

    constructor(
        private $log: ng.ILogService,
        protected $mdDialog: ng.material.IDialogService,
        private $q: ng.IQService,
        private $scope,
        $translate: ng.translate.ITranslateService,
        private organization: thor.Organization,
        private organizationService,
        private quotasService,
        private monextContracts: MonextContract[],
        private monextContractService,
        private toaster
    ) {
        super($mdDialog, $translate);
        $scope.isAdvertiser = () => isAdvertiser(this.organization);
        $scope.isQuotaConsumed = (yearQuota: Quota) => isQuotaConsumed(yearQuota);

        this.organization = angular.copy(organization);

        defaultsDeep(this.organization, { status: STATUS_DEFAULT });
        this.orgaAtlasAccessMaxTokens = organization.atlas_ba_access_max_tokens;
        this.orgaAtlasMaxTokens = organization.atlas_max_tokens;
        this.status = this.getStatus(organization);

        if (this.monextContracts?.length) {
            this.monextJudicialContract = this.monextContracts.find(contract => contract.type === "judicial");
            this.monextVoluntaryContract = this.monextContracts.find(contract => contract.type === "voluntary");
        }

        this.initialMonextVoluntaryContract = angular.copy(this.monextVoluntaryContract);
        this.initialMonextJudicialContract = angular.copy(this.monextJudicialContract);

        if ($scope.isAdvertiser()) {
            quotasService.get(organization.id);
        }
    }

    public getStatus(organization: thor.Organization) {
        const status = organization.status || STATUS_DEFAULT;

        if (status.archive) {
            return Status.Archive;
        }
        if (status.restricted) {
            return Status.Restricted;
        }
    }

    public setStatus() {
        const status = angular.copy(STATUS_DEFAULT);

        status.archive = this.status === Status.Archive;
        status.restricted = this.status === Status.Restricted;

        return status;
    }

    public update(form) {
        if (!form.$valid) {
            return;
        }
        const { atlas_max_tokens, atlas_ba_access_max_tokens, id: organizationId, membership } =
            this.organization || {};
        const status = this.setStatus();
        const promises = [
            this.organizationService.updateByAdmin(organizationId, {
                atlas_max_tokens, atlas_ba_access_max_tokens, status
            }),
            this.organizationService.postMembership(organizationId, membership)
        ];
        this.organization.status = status;

        if (this.monextJudicialContract) {
            const hasNewContract =
                this.monextJudicialContract.provider_infos?.vad_contract_id &&
                this.monextJudicialContract.provider_infos?.point_of_sale_reference;

            const hasNoInitialContract =
                !this.initialMonextJudicialContract?.provider_infos?.vad_contract_id &&
                !this.initialMonextJudicialContract?.provider_infos?.point_of_sale_reference;

            const isDifferent =
                this.initialMonextJudicialContract?.provider_infos?.vad_contract_id !== this.monextJudicialContract.provider_infos?.vad_contract_id ||
                this.initialMonextJudicialContract?.provider_infos?.point_of_sale_reference !== this.monextJudicialContract.provider_infos?.point_of_sale_reference;

            if (hasNewContract && hasNoInitialContract) {
                promises.push(
                    this.monextContractService.create(this.organization.id, "judicial", {
                        point_of_sale_reference: this.monextJudicialContract.provider_infos.point_of_sale_reference,
                        vad_contract_id: this.monextJudicialContract.provider_infos.vad_contract_id
                    })
                );
            } else if (isDifferent) {
                promises.push(
                    this.monextContractService.update(this.organization.id, "judicial", {
                        point_of_sale_reference: this.monextJudicialContract.provider_infos.point_of_sale_reference,
                        vad_contract_id: this.monextJudicialContract.provider_infos.vad_contract_id
                    })
                );
            }
        }


        if (this.monextVoluntaryContract) {
            const hasNewContract =
                this.monextVoluntaryContract.provider_infos?.vad_contract_id &&
                this.monextVoluntaryContract.provider_infos?.point_of_sale_reference;

            const hasNoInitialContract =
                !this.initialMonextVoluntaryContract?.provider_infos?.vad_contract_id &&
                !this.initialMonextVoluntaryContract?.provider_infos?.point_of_sale_reference;

            const isDifferent =
                this.initialMonextVoluntaryContract?.provider_infos?.vad_contract_id !== this.monextVoluntaryContract.provider_infos?.vad_contract_id ||
                this.initialMonextVoluntaryContract?.provider_infos?.point_of_sale_reference !== this.monextVoluntaryContract.provider_infos?.point_of_sale_reference;

            if (hasNewContract && hasNoInitialContract) {
                promises.push(
                    this.monextContractService.create(this.organization.id, "voluntary", {
                        point_of_sale_reference: this.monextVoluntaryContract.provider_infos.point_of_sale_reference,
                        vad_contract_id: this.monextVoluntaryContract.provider_infos.vad_contract_id
                    })
                );
            } else if (isDifferent) {
                promises.push(
                    this.monextContractService.update(this.organization.id, "voluntary", {
                        point_of_sale_reference: this.monextVoluntaryContract.provider_infos.point_of_sale_reference,
                        vad_contract_id: this.monextVoluntaryContract.provider_infos.vad_contract_id
                    })
                );
            }
        }

        if (this.$scope.isAdvertiser()) {
            promises.push(
                this.quotasService.putCurrentYearQuota({
                    organizationId,
                    count: this.quotasService.quotas?.current_year?.quota || 0
                }),
                this.quotasService.putNextYearQuota({
                    organizationId,
                    count: this.quotasService.quotas?.next_year?.quota || 0
                })
            );
        }

        this.$q
            .all(promises)
            .then(() => {
                this.orgaAtlasAccessMaxTokens = this.organization.atlas_ba_access_max_tokens;
                this.orgaAtlasMaxTokens = this.organization.atlas_max_tokens;
                this.toaster.show(this.$t("mise_a_jour_ok"));
            })
            .catch(error => {
                this.organization.atlas_ba_access_max_tokens = this.orgaAtlasAccessMaxTokens;
                this.organization.atlas_max_tokens = this.orgaAtlasMaxTokens;
                this.toaster.show(this.$t("une_erreur_est_survenue_mise_a_jour_impossible"), true);
                this.$log.error(error);
            })
            .finally(() => this.$mdDialog.hide(this.organization));
    }

    public validateMonextContracts(form) {
        if (
            (this.initialMonextVoluntaryContract?.provider_infos?.vad_contract_id && !form.monextVoluntaryContract.$modelValue) ||
            (this.initialMonextVoluntaryContract?.provider_infos?.point_of_sale_reference && !form.monextVoluntaryPointOfSaleReference.$modelValue) ||
            (this.initialMonextJudicialContract?.provider_infos?.vad_contract_id && !form.monextJudicialContract.$modelValue) ||
            (this.initialMonextJudicialContract?.provider_infos?.point_of_sale_reference && !form.monextJudicialPointOfSaleReference.$modelValue))
        {
            form.$setValidity("monextContracts", false);
        } else {
            form.$setValidity("monextContracts", true);
        }
    }
}
